// Fixed cookie declaration. Texts are label keys in SystemLabel group CMS.CookieConsent (fallbacks in consentTexts.ts).
export type ConsentCategory = 'necessary' | 'statistics' | 'marketing';
export const CATEGORIES: ConsentCategory[] = ['necessary', 'statistics', 'marketing'];

export type ProviderKey = 'jirango' | 'google_analytics' | 'meta' | 'youtube' | 'vimeo' | 'dailymotion' | 'google_maps' | 'embedded';
export type StorageType = 'cookie' | 'local' | 'pixel';

export interface CatalogueCookie {
  name: string;
  descriptionKey: string;
  lifetimeKey: string;
  type: StorageType;
}

export interface CatalogueProvider {
  key: ProviderKey;
  /** Proper name, or a label key when nameKey is set. */
  name: string;
  nameKey?: string;
  category: ConsentCategory;
  thirdParty: boolean;
  /** null = use the event's privacy policy link. */
  policyUrl: string | null;
  cookies: CatalogueCookie[];
}

export const CATALOGUE: CatalogueProvider[] = [
  {
    key: 'jirango',
    name: 'Jirango',
    category: 'necessary',
    thirdParty: false,
    policyUrl: null,
    cookies: [
      { name: 'jirango_consent_[id]', descriptionKey: 'CookieJirangoConsent', lifetimeKey: 'Lifetime1Year', type: 'local' },
      { name: 'formstates', descriptionKey: 'CookieJirangoFormstates', lifetimeKey: 'LifetimePersistent', type: 'local' },
      { name: '9983memberCache', descriptionKey: 'CookieJirangoMemberCache', lifetimeKey: 'LifetimePersistent', type: 'local' },
      { name: 'pwd_[id]', descriptionKey: 'CookieJirangoPwd', lifetimeKey: 'LifetimePersistent', type: 'local' },
      { name: 'jgo_preview_[id]', descriptionKey: 'CookieJirangoPreview', lifetimeKey: 'LifetimePreview', type: 'cookie' },
      { name: 'subscription', descriptionKey: 'CookieJirangoPush', lifetimeKey: 'LifetimePersistent', type: 'local' },
      { name: '__dco, __tmpl', descriptionKey: 'CookieJirangoCheckout', lifetimeKey: 'LifetimePersistent', type: 'local' },
    ],
  },
  {
    key: 'google_analytics',
    name: 'Google Analytics',
    category: 'statistics',
    thirdParty: true,
    policyUrl: 'https://policies.google.com/privacy',
    cookies: [
      { name: '_ga', descriptionKey: 'CookieGa', lifetimeKey: 'Lifetime2Years', type: 'cookie' },
      { name: '_ga_[id]', descriptionKey: 'CookieGaId', lifetimeKey: 'Lifetime2Years', type: 'cookie' },
    ],
  },
  {
    key: 'meta',
    name: 'Meta (Facebook)',
    category: 'marketing',
    thirdParty: true,
    policyUrl: 'https://www.facebook.com/privacy/policy/',
    cookies: [
      { name: '_fbp', descriptionKey: 'CookieFbp', lifetimeKey: 'Lifetime3Months', type: 'cookie' },
      { name: 'fr', descriptionKey: 'CookieFr', lifetimeKey: 'Lifetime3Months', type: 'cookie' },
      { name: 'tr', descriptionKey: 'CookieTr', lifetimeKey: 'LifetimeSession', type: 'pixel' },
    ],
  },
  {
    key: 'youtube',
    name: 'YouTube',
    category: 'marketing',
    thirdParty: true,
    policyUrl: 'https://policies.google.com/privacy',
    cookies: [
      { name: 'YSC', descriptionKey: 'CookieYsc', lifetimeKey: 'LifetimeSession', type: 'cookie' },
      { name: 'VISITOR_INFO1_LIVE', descriptionKey: 'CookieVisitorInfo', lifetimeKey: 'Lifetime6Months', type: 'cookie' },
      { name: 'VISITOR_PRIVACY_METADATA', descriptionKey: 'CookieVisitorPrivacy', lifetimeKey: 'Lifetime6Months', type: 'cookie' },
      { name: 'yt-remote-device-id', descriptionKey: 'CookieYtRemote', lifetimeKey: 'LifetimePersistent', type: 'local' },
    ],
  },
  {
    key: 'vimeo',
    name: 'Vimeo',
    category: 'marketing',
    thirdParty: true,
    policyUrl: 'https://vimeo.com/privacy',
    cookies: [
      { name: 'vuid', descriptionKey: 'CookieVuid', lifetimeKey: 'Lifetime2Years', type: 'cookie' },
      { name: '__cf_bm', descriptionKey: 'CookieCfBm', lifetimeKey: 'Lifetime30Minutes', type: 'cookie' },
    ],
  },
  {
    key: 'dailymotion',
    name: 'Dailymotion',
    category: 'marketing',
    thirdParty: true,
    policyUrl: 'https://legal.dailymotion.com/en/privacy-policy/',
    cookies: [
      { name: 'v1st', descriptionKey: 'CookieDmV1st', lifetimeKey: 'Lifetime13Months', type: 'cookie' },
      { name: 'ts', descriptionKey: 'CookieDmTs', lifetimeKey: 'Lifetime13Months', type: 'cookie' },
    ],
  },
  {
    key: 'google_maps',
    name: 'Google Maps',
    category: 'marketing',
    thirdParty: true,
    policyUrl: 'https://policies.google.com/privacy',
    cookies: [{ name: 'NID', descriptionKey: 'CookieNid', lifetimeKey: 'Lifetime6Months', type: 'cookie' }],
  },
  {
    key: 'embedded',
    name: 'Embedded content',
    nameKey: 'ProviderEmbedded',
    category: 'marketing',
    thirdParty: true,
    policyUrl: null,
    cookies: [{ name: '*', descriptionKey: 'CookieEmbedded', lifetimeKey: 'LifetimeUnknown', type: 'cookie' }],
  },
];

const PROVIDER_KEYS = CATALOGUE.map(p => p.key);

export const isProviderKey = (value: unknown): value is ProviderKey => PROVIDER_KEYS.includes(value as ProviderKey);

export const categoryOf = (key: ProviderKey): ConsentCategory => CATALOGUE.find(p => p.key === key)?.category ?? 'marketing';
